import React, { ReactNode, useState } from 'react';
import {
  BoxV2 as Box,
  Flex,
  MultimediaList,
  MultimediaUploader,
  MimeTypes,
  AttachmentInfo,
} from 'portal-commons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpFromBracket } from '@fortawesome/pro-light-svg-icons';
import { capitalize } from 'lodash-es';
import {
  getImageDimensions,
  isDimensionsNotMatch,
  isDuplicateFilename,
  isFileNameInvalid,
  isFileSizeExceeded,
} from './utils';
import UploadErrorDialog from './UploadErrorDialog';
import {
  AttachmentFolder,
  BrandAssetType,
  attachmentController,
} from '@/apis/core';

const AcceptedMimeTypes = {
  '.jpeg': 'image/jpeg',
  '.jpg': 'image/jpeg',
  '.png': 'image/png',
};

interface Props {
  brandAssetType: BrandAssetType;
  dimensions?: {
    width: number;
    height: number;
  };
  fileLimitKB?: number;
  thumbnailWidth: number;
  acceptedMimeTypes?: MimeTypes;
  uploadPermission?: boolean;
  deletePermission?: boolean;
  files?: AttachmentInfo[];
  onChange?: (files: AttachmentInfo[]) => void;
  onProcessingChange?: (isProcessing: boolean) => void;
  onPreview: (attachment: AttachmentInfo) => void;
}

const AssetImageUpload: React.FC<Props> = ({
  brandAssetType,
  dimensions,
  fileLimitKB,
  acceptedMimeTypes = AcceptedMimeTypes,
  uploadPermission = true,
  deletePermission = true,
  files = [],
  thumbnailWidth,
  onChange,
  onProcessingChange,
  onPreview,
}) => {
  const [listLoading, setListLoading] = useState(false);
  const [openError, setOpenError] = useState(false);
  const [errorContent, setErrorContent] = useState<string | ReactNode>('');
  const [processing, setProcessing] = useState(false);
  const assetType = capitalize(brandAssetType);

  const handleFileTypeError = () => {
    openErrorDialog('The uploaded file type is not supported');
  };

  const updateProcessing = (isProcessing: boolean) => {
    setProcessing(isProcessing);
    onProcessingChange?.(isProcessing);
  };

  const onFileChange = async (file: File) => {
    setListLoading(true);
    updateProcessing(true);
    const fileName =
      file.name.substring(0, file.name.lastIndexOf('.')) || file.name;
    if (isDuplicateFilename(fileName, files)) {
      openErrorDialog(
        'The uploaded file name already exists. Please choose a different name.'
      );
      return;
    }
    if (isFileNameInvalid(fileName)) {
      openErrorDialog(
        <>
          The uploaded file name is invalid <br />
          Must be between 3 and 50 characters and contain only letters, numbers,
          spaces, hyphens, and underscores
        </>
      );
      return;
    }
    if (fileLimitKB && isFileSizeExceeded(file, fileLimitKB)) {
      openErrorDialog('The uploaded file size exceeds the limit');
      return;
    }
    if (dimensions) {
      const image = await getImageDimensions(file);
      if (isDimensionsNotMatch(image, dimensions)) {
        const media = `${assetType}s`;
        openErrorDialog(
          <>
            The uploaded file does not have the correct dimensions. <br />
            {media} must be {dimensions.width}px x {dimensions.height}px to
            upload.
          </>
        );
        return;
      }
    }

    const uploadFile = await attachmentController.uploadAttachment({
      folder:
        brandAssetType === BrandAssetType.Logo
          ? AttachmentFolder.BrandAssetLogo
          : AttachmentFolder.BrandAssetBanner,
      file,
    });
    if (uploadFile) {
      const attachment: AttachmentInfo = {
        uuid: uploadFile.uuid,
        fileName,
        mimeType: uploadFile.mimeType,
        file,
      };
      onChange?.([...files, attachment]);
    }

    updateProcessing(false);
    setListLoading(false);
  };

  const openErrorDialog = (content: string | ReactNode) => {
    setListLoading(false);
    updateProcessing(false);
    setOpenError(true);
    setErrorContent(content);
  };

  const onDeleteFile = (idx: number) => {
    onChange?.(files.filter((_, i) => i !== idx));
  };

  const customAdditionalBlock = () => {
    const unit = fileLimitKB && fileLimitKB > 1024 ? 'MB' : 'KB';
    const fileLimit =
      fileLimitKB && fileLimitKB > 1024
        ? (fileLimitKB / 1024).toFixed(2)
        : fileLimitKB;
    const acceptedFormats = Object.keys(acceptedMimeTypes).reduce(
      (acc, key, idx, arr) => {
        key = key.replace('.', '').toUpperCase();
        if (arr.length === 1) {
          return key;
        } else if (idx === arr.length - 1) {
          return `${acc} or ${key}`;
        } else if (idx === arr.length - 2) {
          return `${acc}${key}`;
        } else {
          return `${acc}${key}, `;
        }
      },
      ''
    );

    return (
      <Flex
        sx={{
          flexDirection: 'column',
          alignItems: 'center',
          fontSize: 'H200',
          fontWeight: 400,
          lineHeight: 'normal',
          color: 't.grey300',
          textAlign: 'center',
        }}
      >
        {dimensions && (
          <span>
            File dimensions {dimensions.width}x{dimensions.height}px
          </span>
        )}
        {fileLimitKB && (
          <span>
            Max file size of {fileLimit} {unit}
          </span>
        )}
        <span>
          Accepted formats: <br />
          {acceptedFormats}
        </span>
      </Flex>
    );
  };

  return (
    <Flex sx={{ gap: 10 }}>
      {uploadPermission && (
        <Box sx={{ width: 220, minHeight: 240 }}>
          <MultimediaUploader
            onChange={(file) => onFileChange(file)}
            onTypeError={handleFileTypeError}
            supportedMimeTypes={AcceptedMimeTypes}
            icon={<FontAwesomeIcon icon={faArrowUpFromBracket} size="2x" />}
            buttonText="Click here to Upload"
            disabled={processing}
            disabledText="Processing..."
            description={<Box>Drop files to attach or...</Box>}
            additional={customAdditionalBlock()}
            style={{
              minWidth: '220px',
              minHeight: '100%',
              padding: 30,
              justifyContent: 'center',
              gap: '10px',
              border: '1px dashed #949495',
              borderRadius: '5px',
            }}
            data-testid="assetsUploader"
          />
        </Box>
      )}
      <Box sx={{ flex: 1, height: 240 }}>
        <MultimediaList
          editable={deletePermission}
          thumbnail
          thumbnailWidth={thumbnailWidth}
          downloadable={false}
          title={`${assetType} Files`}
          emptyMessage="No files have been attached"
          loading={listLoading}
          attachments={files}
          onSelect={(idx: number) => onPreview(files[idx])}
          onDelete={onDeleteFile}
          data-testid="assetsList"
        />
      </Box>
      <UploadErrorDialog
        open={openError}
        content={errorContent}
        onClose={() => setOpenError(false)}
      />
    </Flex>
  );
};

export default AssetImageUpload;
